import { createSelector } from 'reselect';
import { flow, pipe } from 'fp-ts/function';
import { array, nonEmptyArray, option, record } from 'fp-ts';
import { createAction } from '@mews/framework';
import { getStringProp, primitiveTypeUniq, values } from '@mews/fp';
import { ApplicationState } from '@mews-commander/types';
import { ResourceCategoryAssignments } from '@mews-commander/resources';
import parentSelector from '../selector';
import prefix from '../prefix';
import { createEntitiesReducer, createBaseEntitySelectors, createEntityPropertyIncludesSelector } from './utils';
import { getReservationsByIds } from './reservations';

export const NAME = 'resourceCategoryAssignments';

export const RESOURCE_CATEGORY_ASSIGNMENTS_UPDATED = prefix('RESOURCE_CATEGORY_ASSIGNMENTS_UPDATED');

export const [
    getResourceCategoryAssignments,
    getResourceCategoryAssignmentByIds,
    getResourceCategoryAssignmentByOwnerId,
    getResourceCategoryAssignment,
] = createBaseEntitySelectors<ApplicationState, ResourceCategoryAssignments, typeof NAME>(parentSelector, NAME);

export const getResourceCategoryAssignmentsByResourceIds = createSelector(
    getStringProp('serviceId'),
    createEntityPropertyIncludesSelector(getResourceCategoryAssignments, 'resourceId', 'resourceIds'),
    (serviceId, resourceCateroryAssignments) =>
        resourceCateroryAssignments.filter(
            (resourceCategoryAssignment) => resourceCategoryAssignment.serviceId === serviceId
        )
);

export const getResourceIdsByResourceCategoryIds = createSelector(
    getStringProp('serviceId'),
    createEntityPropertyIncludesSelector(getResourceCategoryAssignments, 'categoryId', 'resourceCategoryIds'),
    (serviceId, resourceCategoryAssignments) =>
        pipe(
            resourceCategoryAssignments,
            array.filterMap(({ resourceId, serviceId: assignmentServiceId }) =>
                assignmentServiceId === serviceId ? option.some(resourceId) : option.none
            )
        )
);

export const getResourceCategoryAssignmentIdMappingByServiceIdByResourceIdSelector = createSelector(
    getResourceCategoryAssignments,
    flow(
        Object.values<ResourceCategoryAssignments>,
        nonEmptyArray.groupBy(({ serviceId }) => serviceId),
        record.map(nonEmptyArray.groupBy(({ resourceId }) => resourceId)),
        (assignmentMap) =>
            (serviceId: string, resourceId: string): ResourceCategoryAssignments | undefined =>
                assignmentMap?.[serviceId]?.[resourceId]?.[0]
    )
);

export const getResourceCategoryAssignmentByResourceId = createSelector(
    getStringProp('resourceId'),
    getStringProp('serviceId'),
    getResourceCategoryAssignmentIdMappingByServiceIdByResourceIdSelector,
    (resourceId, serviceId, mapping) => mapping(serviceId, resourceId)
);

export const setResourceCategoryAssignments = (entities: ResourceCategoryAssignments[]) =>
    createAction(RESOURCE_CATEGORY_ASSIGNMENTS_UPDATED, entities);

export const resourceCategoryAssignmentsReducer = createEntitiesReducer({
    UPDATE_SUCCESS: RESOURCE_CATEGORY_ASSIGNMENTS_UPDATED,
});

export const getResourceCategoryIdByResourceId = flow(
    getResourceCategoryAssignmentByResourceId,
    (resourceCategoryAssignment) => resourceCategoryAssignment?.categoryId
);

export const getReservationsAssignedCategoryIdsMap = (
    state: ApplicationState,
    params: { reservationIds: string[]; enterpriseId: string }
) => {
    const reservations = getReservationsByIds(state, { ids: params.reservationIds, enterpriseId: params.enterpriseId });

    return pipe(
        reservations,
        array.reduce({} as Record<string, string>, (acc, r) => {
            const id =
                r.assignedResourceId &&
                getResourceCategoryIdByResourceId(state, {
                    resourceId: r.assignedResourceId,
                    serviceId: r.serviceId,
                });

            return { ...acc, [r.id]: id ?? r.requestedCategoryId };
        })
    );
};

export const getReservationsAssignedCategoryIds = (
    state: ApplicationState,
    params: { reservationIds: string[]; enterpriseId: string }
): string[] => pipe(getReservationsAssignedCategoryIdsMap(state, params), values, primitiveTypeUniq);
